/**
 * The portfolio store — a cache of the server's truth, not the truth itself.
 *
 * Portfolios used to live here, in localStorage: clearing site data lost them, and a
 * second browser saw an empty list. They now live in Postgres, keyed to the account,
 * so this store holds what the API last said and hands every mutation to the server.
 *
 * The rule that makes that safe: a mutation does not patch its own copy. It calls the
 * API and replaces the portfolio with the one the server answered with — the server
 * re-derives a holding's quantity and average from its buy log, so a client that
 * guessed the result could disagree with the row it had just written.
 *
 * `activePortfolioId` is the exception: which portfolio you are looking at is a
 * property of this device, not of the account, so it stays local and is never sent.
 */

import { create } from 'zustand';
import type {
  Portfolio,
  Holding,
  CreatePortfolioInput,
  UpdatePortfolioInput,
  CreateHoldingInput,
  UpdateHoldingInput,
  DividendRecord,
  BuyInput,
  SellInput,
  DeclaredDividendInput,
  CorporateActionInput,
  DeleteBuyResult,
} from '@/types';
import { PORTFOLIO_COLORS, STORAGE_KEYS } from '@/constants';
import { portfoliosApi } from '@/services';
import { ApiError } from '@/services/http';

interface PortfolioState {
  portfolios: Portfolio[];
  activePortfolioId: string | null;
  /** True while the first load is in flight, so the UI can wait instead of flashing empty. */
  isLoading: boolean;
  /** The most recent failure, in words a person can read. Cleared by the next success. */
  error: string | null;
  /** Whether the server's list has been fetched for the signed-in account. */
  loaded: boolean;

  /** Fetch the signed-in account's portfolios. Called once the session is known. */
  load: () => Promise<void>;
  /** Forget everything on sign-out, so the next account never sees the last one's rows. */
  reset: () => void;
  /** Put a portfolio the server just returned in place of the stale copy. */
  put: (portfolio: Portfolio) => void;

  // Portfolio CRUD
  createPortfolio: (input: CreatePortfolioInput) => Promise<Portfolio>;
  updatePortfolio: (input: UpdatePortfolioInput) => Promise<void>;
  deletePortfolio: (id: string) => Promise<void>;
  duplicatePortfolio: (id: string) => Promise<Portfolio>;
  archivePortfolio: (id: string) => Promise<void>;
  restorePortfolio: (id: string) => Promise<void>;
  setActivePortfolio: (id: string | null) => void;

  // Holding CRUD
  /** Add a holding. The server seeds its buy log, so the answer already carries one. */
  addHolding: (input: CreateHoldingInput) => Promise<Holding | undefined>;
  /**
   * Edit a holding. Its quantity, average and date are expressed through the buy log
   * server-side (decision 14), and an edit the log cannot represent is refused with a
   * 409 — the holding is then left exactly as it was.
   */
  updateHolding: (portfolioId: string, input: UpdateHoldingInput) => Promise<void>;
  /**
   * Buy more of a stock already held. The server re-derives the position from the
   * whole log, so the average that comes back is the log's, not a blend done here.
   */
  buyInto: (portfolioId: string, input: BuyInput) => Promise<Holding | undefined>;
  /**
   * Correct one logged purchase; fixing a single trade re-prices the position from
   * the log. Returns the re-derived holding so the caller can report the new average.
   */
  updateBuy: (
    portfolioId: string,
    holdingId: string,
    buyId: string,
    patch: { shares: number; pricePerShare: number }
  ) => Promise<Holding | undefined>;
  /**
   * Remove a logged purchase and re-derive the position from what is left. The last
   * purchase is the position itself, so removing it removes the holding — an empty
   * log would describe nothing.
   */
  deleteBuy: (
    portfolioId: string,
    holdingId: string,
    buyId: string
  ) => Promise<DeleteBuyResult | undefined>;
  /**
   * Sell part of a held stock. The server replays buys + sells to derive the
   * position and realized P/L; an oversell is refused with a 409.
   */
  sell: (portfolioId: string, input: SellInput) => Promise<Holding | undefined>;
  /** Correct one logged sale, then re-derive the position from the whole ledger. */
  updateSell: (
    portfolioId: string,
    holdingId: string,
    sellId: string,
    patch: { date: string; shares: number; pricePerShare: number; fees: number }
  ) => Promise<Holding | undefined>;
  /** Remove a logged sale and re-derive the position from what is left. */
  deleteSell: (portfolioId: string, holdingId: string, sellId: string) => Promise<void>;
  deleteHolding: (portfolioId: string, holdingId: string) => Promise<void>;

  // Cash
  addCash: (
    portfolioId: string,
    input: { date: string; type: 'deposit' | 'withdrawal'; amount: number; notes?: string }
  ) => Promise<void>;
  deleteCash: (portfolioId: string, cashId: string) => Promise<void>;

  // Dividend
  addDividend: (
    portfolioId: string,
    holdingId: string,
    record: Omit<DividendRecord, 'id' | 'holdingId'>
  ) => Promise<void>;
  deleteDividend: (portfolioId: string, holdingId: string, dividendId: string) => Promise<void>;
  addDeclaredDividend: (
    portfolioId: string,
    holdingId: string,
    input: Omit<DeclaredDividendInput, 'holdingId'>
  ) => Promise<void>;
  updateDeclaredDividend: (
    portfolioId: string,
    holdingId: string,
    dividendId: string,
    patch: Omit<DeclaredDividendInput, 'holdingId'>
  ) => Promise<void>;
  deleteDeclaredDividend: (portfolioId: string, holdingId: string, dividendId: string) => Promise<void>;
  addCorporateAction: (
    portfolioId: string,
    holdingId: string,
    input: Omit<CorporateActionInput, 'holdingId'>
  ) => Promise<void>;
  deleteCorporateAction: (portfolioId: string, holdingId: string, actionId: string) => Promise<void>;

  // Selectors — read from the cache, never the network
  getPortfolioById: (id: string) => Portfolio | undefined;
  getHoldingById: (portfolioId: string, holdingId: string) => Holding | undefined;
  getAllSymbols: () => string[];

  // Import / Export
  /** Restore a backup through the server, which decides what a re-import means. */
  importPortfolios: (portfolios: Portfolio[]) => Promise<{ added: number; updated: number }>;
  /**
   * How many portfolios this browser still holds from before the move to Postgres.
   * Counted, never sent: uploading somebody's old rows without them asking would be
   * a surprise, so the count is shown and the import is a deliberate act.
   */
  countBrowserPortfolios: () => number;
  /** Send those portfolios to the account, then clear them out of the browser. */
  importFromBrowser: () => Promise<{ added: number; updated: number }>;
  /** Everything the account owns, for a file the user keeps. */
  exportPortfolios: () => Promise<Portfolio[]>;
  /** Delete every portfolio the account owns. Irreversible: the caller confirms first. */
  clearAll: () => Promise<void>;
}

/** The message a person should see for a failure, whoever reported it. */
const messageFor = (err: unknown): string => {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
};

const holdingIn = (portfolios: Portfolio[], portfolioId: string, holdingId: string) =>
  portfolios.find((p) => p.id === portfolioId)?.holdings.find((h) => h.id === holdingId);

/**
 * The rows this browser persisted before portfolios lived on the server.
 *
 * Zustand's `persist` wrote `{ state: { portfolios }, version }`, so that is the shape
 * to read — defensively, because a half-written or hand-edited entry must not break the
 * page that offers to rescue it. Old rows predate `buys`, so each holding gets an empty
 * log, which the server seeds from its totals.
 */
function readBrowserPortfolios(): Portfolio[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PORTFOLIOS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { state?: { portfolios?: Portfolio[] } };
    const portfolios = parsed?.state?.portfolios;
    if (!Array.isArray(portfolios)) return [];
    return portfolios.map((portfolio) => ({
      ...portfolio,
      holdings: (portfolio.holdings ?? []).map((holding) => ({
        ...holding,
        buys: holding.buys ?? [],
        sells: holding.sells ?? [],
        declaredDividends: holding.declaredDividends ?? [],
        corporateActions: holding.corporateActions ?? [],
      })),
    }));
  } catch {
    return [];
  }
}

function clearBrowserPortfolios() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEYS.PORTFOLIOS);
  } catch {
    /* nothing to do: the rows are on the server now, and the key is inert */
  }
}

export const usePortfolioStore = create<PortfolioState>()((set, get) => ({
  portfolios: [],
  activePortfolioId: null,
  isLoading: false,
  error: null,
  loaded: false,

  put: (portfolio) =>
    set((state) => ({
      portfolios: state.portfolios.some((p) => p.id === portfolio.id)
        ? state.portfolios.map((p) => (p.id === portfolio.id ? portfolio : p))
        : [...state.portfolios, portfolio],
      error: null,
    })),

  load: async () => {
    set({ isLoading: true, error: null });
    try {
      const portfolios = await portfoliosApi.list();
      set({ portfolios, isLoading: false, loaded: true, error: null });
    } catch (err) {
      // A failed load is not an empty account: keep what we have and say what happened.
      set({ isLoading: false, error: messageFor(err) });
    }
  },

  reset: () =>
    set({ portfolios: [], activePortfolioId: null, isLoading: false, error: null, loaded: false }),

  // ─── Portfolio CRUD ──────────────────────────────────────────────────────
  createPortfolio: async (input) => {
    try {
      const colorIndex = get().portfolios.length % PORTFOLIO_COLORS.length;
      const portfolio = await portfoliosApi.create({
        ...input,
        color: input.color || PORTFOLIO_COLORS[colorIndex],
      });
      set((state) => ({ portfolios: [...state.portfolios, portfolio], error: null }));
      return portfolio;
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  updatePortfolio: async (input) => {
    try {
      get().put(await portfoliosApi.update(input.id, input));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deletePortfolio: async (id) => {
    try {
      await portfoliosApi.remove(id);
      set((state) => ({
        portfolios: state.portfolios.filter((p) => p.id !== id),
        activePortfolioId: state.activePortfolioId === id ? null : state.activePortfolioId,
        error: null,
      }));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  duplicatePortfolio: async (id) => {
    try {
      const duplicate = await portfoliosApi.duplicate(id);
      set((state) => ({ portfolios: [...state.portfolios, duplicate], error: null }));
      return duplicate;
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  archivePortfolio: async (id) => {
    try {
      const portfolio = await portfoliosApi.archive(id);
      get().put(portfolio);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  restorePortfolio: async (id) => {
    try {
      const portfolio = await portfoliosApi.restore(id);
      get().put(portfolio);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  setActivePortfolio: (id) => set({ activePortfolioId: id }),

  // ─── Holding CRUD ────────────────────────────────────────────────────────
  addHolding: async (input) => {
    try {
      const before = new Set(
        (get().portfolios.find((p) => p.id === input.portfolioId)?.holdings ?? []).map((h) => h.id)
      );
      const portfolio = await portfoliosApi.addHolding(input.portfolioId, input);
      get().put(portfolio);
      return portfolio.holdings.find((h) => !before.has(h.id));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  updateHolding: async (portfolioId, input) => {
    try {
      get().put(await portfoliosApi.updateHolding(portfolioId, input));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  buyInto: async (portfolioId, input) => {
    try {
      const portfolio = await portfoliosApi.addBuy(portfolioId, input.holdingId, {
        date: input.date,
        shares: input.shares,
        pricePerShare: input.pricePerShare,
      });
      get().put(portfolio);
      return portfolio.holdings.find((h) => h.id === input.holdingId);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  updateBuy: async (portfolioId, holdingId, buyId, patch) => {
    try {
      const portfolio = await portfoliosApi.updateBuy(portfolioId, holdingId, buyId, patch);
      get().put(portfolio);
      return portfolio.holdings.find((h) => h.id === holdingId);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteBuy: async (portfolioId, holdingId, buyId) => {
    try {
      const { portfolio, holdingRemoved } = await portfoliosApi.removeBuy(
        portfolioId,
        holdingId,
        buyId
      );
      get().put(portfolio);
      if (holdingRemoved) return { kind: 'holding-removed' };
      const holding = portfolio.holdings.find((h) => h.id === holdingId);
      return holding ? { kind: 'updated', holding } : { kind: 'holding-removed' };
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  sell: async (portfolioId, input) => {
    try {
      const portfolio = await portfoliosApi.addSell(portfolioId, input.holdingId, {
        date: input.date,
        shares: input.shares,
        pricePerShare: input.pricePerShare,
        fees: input.fees,
      });
      get().put(portfolio);
      return portfolio.holdings.find((h) => h.id === input.holdingId);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  updateSell: async (portfolioId, holdingId, sellId, patch) => {
    try {
      const portfolio = await portfoliosApi.updateSell(portfolioId, holdingId, sellId, patch);
      get().put(portfolio);
      return portfolio.holdings.find((h) => h.id === holdingId);
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteSell: async (portfolioId, holdingId, sellId) => {
    try {
      get().put(await portfoliosApi.removeSell(portfolioId, holdingId, sellId));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  addCash: async (portfolioId, input) => {
    try {
      get().put(await portfoliosApi.addCash(portfolioId, input));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteCash: async (portfolioId, cashId) => {
    try {
      get().put(await portfoliosApi.removeCash(portfolioId, cashId));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteHolding: async (portfolioId, holdingId) => {
    try {
      get().put(await portfoliosApi.removeHolding(portfolioId, holdingId));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  // ─── Dividends ───────────────────────────────────────────────────────────
  addDividend: async (portfolioId, holdingId, record) => {
    try {
      get().put(await portfoliosApi.addDividend(portfolioId, holdingId, record));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteDividend: async (portfolioId, holdingId, dividendId) => {
    try {
      get().put(await portfoliosApi.removeDividend(portfolioId, holdingId, dividendId));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  addDeclaredDividend: async (portfolioId, holdingId, input) => {
    try {
      get().put(await portfoliosApi.addDeclaredDividend(portfolioId, holdingId, input));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  updateDeclaredDividend: async (portfolioId, holdingId, dividendId, patch) => {
    try {
      get().put(
        await portfoliosApi.updateDeclaredDividend(portfolioId, holdingId, dividendId, patch),
      );
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteDeclaredDividend: async (portfolioId, holdingId, dividendId) => {
    try {
      get().put(
        await portfoliosApi.removeDeclaredDividend(portfolioId, holdingId, dividendId),
      );
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  addCorporateAction: async (portfolioId, holdingId, input) => {
    try {
      get().put(await portfoliosApi.addCorporateAction(portfolioId, holdingId, input));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  deleteCorporateAction: async (portfolioId, holdingId, actionId) => {
    try {
      get().put(await portfoliosApi.removeCorporateAction(portfolioId, holdingId, actionId));
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  // ─── Selectors ───────────────────────────────────────────────────────────
  getPortfolioById: (id) => get().portfolios.find((p) => p.id === id),

  getHoldingById: (portfolioId, holdingId) => holdingIn(get().portfolios, portfolioId, holdingId),

  getAllSymbols: () => {
    const symbols = new Set<string>();
    get().portfolios.forEach((p) => p.holdings.forEach((h) => symbols.add(h.symbol)));
    return Array.from(symbols);
  },

  // ─── Import / Export ─────────────────────────────────────────────────────
  importPortfolios: async (portfolios) => {
    try {
      // The server answers with the list as it now stands, so there is nothing to merge
      // here — and importing the same backup twice cannot double anything.
      const { portfolios: after, imported } = await portfoliosApi.importAll(portfolios);
      set({ portfolios: after, loaded: true, error: null });
      return { added: imported, updated: 0 };
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  countBrowserPortfolios: () => readBrowserPortfolios().length,

  importFromBrowser: async () => {
    const stale = readBrowserPortfolios();
    if (!stale.length) return { added: 0, updated: 0 };
    try {
      const counts = await get().importPortfolios(stale);
      // Only once the server has them: a failed upload must not lose the only copy.
      clearBrowserPortfolios();
      return counts;
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  exportPortfolios: async () => {
    try {
      const portfolios = await portfoliosApi.exportAll();
      set({ error: null });
      return portfolios;
    } catch (err) {
      set({ error: messageFor(err) });
      throw err;
    }
  },

  clearAll: async () => {
    try {
      for (const id of get().portfolios.map((p) => p.id)) {
        await portfoliosApi.remove(id);
      }
      set({ portfolios: [], activePortfolioId: null, error: null });
    } catch (err) {
      // Some rows are already gone, so the cache is not to be trusted: say what
      // happened and resync from the server.
      set({ error: messageFor(err) });
      await get().load();
      throw err;
    }
  },
}));
